import { lazy, Suspense } from "react";
import { Anchor, Footprints, Bus } from "lucide-react";
import { useDomainConfig } from "@/contexts/DomainConfigContext";
import { useLanguage } from "@/contexts/LanguageContext";

const heroImageDesktop = "/images/southampton-hero-desktop.webp";
const heroImageMobile = "/images/southampton-hero-mobile.webp";

const BookingForm = lazy(() => import("./BookingForm"));

const HeroSection = () => {
  const { config } = useDomainConfig();
  const { t, td } = useLanguage();
  const webName = "Southampton Port Parking";

  return (
    <section className="relative min-h-screen pt-20 md:pt-24 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <picture>
          <source media="(min-width: 768px)" srcSet={heroImageDesktop} width={960} height={640} />
          <source media="(max-width: 767px)" srcSet={heroImageMobile} width={768} height={512} />
          <img
            src={heroImageDesktop}
            alt={td(webName)}
            width={960}
            height={640}
            className="w-full h-full object-cover"
            fetchPriority="high"
            decoding="async"
            loading="eager"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/75 to-primary/50" />
      </div>

      {/* Content */}
      <div className="relative container mx-auto px-4 py-8 sm:py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[calc(100vh-6rem)] lg:min-h-[calc(100vh-8rem)]">
          {/* Left Content */}
          <div className="text-primary-foreground animate-slide-in-left text-center lg:text-left">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
              Southampton Port Parking for Cruise Travellers
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/85 max-w-xl mx-auto lg:mx-0 text-justify">
              Find trusted Southampton Port parking with secure Park and Stroll and Park and Ride services. Enjoy simple arrivals, quick terminal access, and affordable parking for cruise and ferry travel.
            </p>
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mt-5">
              <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-5 py-2.5 border border-white/20">
                <Footprints className="w-5 h-5 text-accent" />
                <span className="text-white font-semibold text-sm">Park &amp; Stroll</span>
              </div>
              <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-5 py-2.5 border border-white/20">
                <Bus className="w-5 h-5 text-accent" />
                <span className="text-white font-semibold text-sm">Park &amp; Ride</span>
              </div>
              <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-5 py-2.5 border border-white/20">
                <Anchor className="w-5 h-5 text-accent" />
                <span className="text-white font-semibold text-sm">Cruise Parking</span>
              </div>
            </div>
          </div>

          {/* Right Content - Booking Form */}
          <div id="booking" className="flex justify-center lg:justify-end scroll-mt-24" style={{ contain: "layout style", minHeight: "600px" }}>
            <Suspense
              fallback={
                <div className="booking-card w-full max-w-md min-h-[600px] animate-pulse bg-white/10 rounded-2xl" />
              }
            >
              <BookingForm />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
